import { create } from 'zustand'

import type { StoredConversation } from '@/features/mitra/api/shelf'
import type { AppPage } from '@/features/mitra/lib/appPage'

/**
 * The panel's DOM id, for the launchers' `aria-controls`. Named here rather
 * than in MitraPanel.tsx because every launcher already imports this store
 * and none of them should have to import the panel to point at it.
 */
export const MITRA_PANEL_ID = 'mitra-panel'

/**
 * The Ask Mitra bar's own box — the field and, while it has focus, the
 * questions over it. Named here for the same reason as the panel id: the
 * right rail measures it to keep its last block clear wherever the two
 * overlap, and should not have to import the panel to find it.
 */
export const MITRA_DOCK_SELECTOR = '[data-mitra-dock]'

/**
 * What an "Ask Mitra" entry point on an app page is about (mobile parity,
 * 2026-10-13): a company (the stock page's header, a holdings row), the
 * market (the NEPSE card) or a news story, with its company where the story
 * names one. An identifier and a headline, never a question: the panel
 * words the question in the reader's register.
 */
export type AskAbout =
  | { topic: 'company'; symbol: string }
  | { topic: 'market' }
  | { topic: 'news'; headline: string; symbol?: string | null }

interface MitraState {
  open: boolean
  /**
   * A question a launcher handed over that OPENED the panel — the Home
   * card's field, a suggestion chip, reaching a closed panel. Held until the
   * panel takes it, so the launcher does not need the panel to be mounted,
   * or even to exist. Safe to ask the instant it is taken: nothing was on
   * screen yet to lose.
   */
  pendingQuestion: string | null
  /**
   * The same kind of hand-over, but aimed at a panel that was ALREADY open.
   * A member may have an unsent draft sitting in the composer, or a turn
   * streaming — asking immediately would discard one or interrupt the
   * other, so a hand-over never reaches `pendingQuestion` in that case.
   * Kept on a separate channel, rather than a flag beside
   * `pendingQuestion`, so a reader of this store cannot treat the two the
   * same by accident: only `pendingQuestion` is ever meant to start a turn
   * on its own.
   */
  draftQuestion: string | null
  /**
   * An entry point's hand-over, held until the panel takes it. NEVER ASKS:
   * on an empty chat the panel opens scoped to the company and fills the
   * composer; with a conversation running it fills the composer of that same
   * conversation (owner: "Ask about X" stays in the running conversation and
   * never opens a new one).
   */
  aboutRequest: AskAbout | null
  /**
   * The title of the page on screen — `ScreenLayout`'s `pageTitle`, the
   * TitleRow's own words — or `null` on a page that names none. What the
   * Ask Mitra bar's context chip names, and what it puts in front of a
   * question asked there ("About Overview: …"). Published by the page
   * through `showPage`, never written by the bar, so there is one source
   * for it.
   */
  pageTitle: string | null
  /**
   * Publishes the page on screen; returns the call that takes it back. The
   * take-back only clears a title this call published, so a page leaving
   * after the next one has already arrived cannot blank the newcomer's.
   */
  showPage: (title: string) => () => void
  /**
   * The company whose page is on screen (the stock page's ticker), or `null`
   * on every other page. Published by that page through `showCompany`, like
   * `pageTitle`, so the panel never reads a ticker off a URL.
   */
  companyPage: string | null
  /** Publishes the company page on screen; returns the call that takes it back, as `showPage` does. */
  showCompany: (symbol: string) => () => void
  /**
   * The company page the panel was OPENED on, held while it stays open.
   * Opening the chat leaves the page for `/chat`, which takes `companyPage`
   * back, so this is what keeps "yo kina ghatyo?" about the company the
   * reader came from (`AskRequest.page`). Cleared when the panel closes.
   */
  openedFrom: string | null
  /**
   * The page of the app on screen, read off the route (`lib/appPage.ts`), or
   * `null` at `/chat` and on a route no family claims. Set by
   * `useMitraRoute`, the one place that follows the route.
   */
  routePage: AppPage | null
  setRoutePage: (page: AppPage | null) => void
  /**
   * The page the panel was OPENED on, held while it stays open, for
   * `openedFrom`'s reason: opening the chat leaves the page for `/chat`, and a
   * question asked there is still about the page the reader came from
   * (`AskRequest.page`), with the screen's title it had then. Cleared when
   * the panel closes.
   */
  openedOnPage: OpenedPage | null
  openPanel: (question?: string) => void
  /** Opens the panel about a company, the market or a story; see `aboutRequest`. */
  askAbout: (about: AskAbout) => void
  /** Hands the entry point's request over exactly once. */
  takeAboutRequest: () => AskAbout | null
  /**
   * Opens the panel with `draft` in the composer, focused, the caret at its
   * end — and never sends it: the member finishes the question and sends it
   * themselves. The Ask Mitra bar hands its words over this way in a build
   * that cannot ask ("About Overview: …"), rather than asking them.
   *
   * Through `draftQuestion`, the one channel that fills without asking, so
   * a draft already in the composer is kept as typed (the composer's own
   * rule). A CLOSED panel has no composer until it draws, and the panel
   * takes a draft the moment one lands, so from closed this opens first and
   * hands the draft over a microtask later — after React has committed the
   * open panel and its composer.
   */
  openWithDraft: (draft: string) => void
  closePanel: () => void
  /** Hands the pending question over exactly once. */
  takePendingQuestion: () => string | null
  /** Hands the draft question over exactly once. */
  takeDraftQuestion: () => string | null
  /**
   * Whether a Stage is open beside the panel at `lg` and up, and whether it
   * has taken the full width (stage foundation design §3.2.4). AppShell
   * reads it to lay the Stage out in the main content area; the panel is
   * what opens and closes it. Always `closed` while `config.mitraStage` is
   * off, so the shell draws exactly what it did.
   */
  stage: 'closed' | 'open' | 'expanded'
  setStage: (stage: 'closed' | 'open' | 'expanded') => void
  /** The element AppShell draws the Stage into at `lg`; null when none is mounted. */
  stageHost: HTMLElement | null
  setStageHost: (host: HTMLElement | null) => void
  /**
   * Which conversation the panel holds. AppShell keys the panel on it, so
   * `newChat` starts a fresh one by drawing a fresh panel: nothing of the old
   * thread — its turns, its conversation id, an open Stage — can leak into
   * the new one. The old one stays in History.
   */
  chat: number
  newChat: () => void
  /**
   * A stored conversation opened from History while the panel already held
   * one (owner, 2026-10-26: recent chats open the session in the chat). It
   * opens as `newChat` does, in a fresh panel, so nothing of the thread on
   * screen leaks into the one reopened, and the fresh panel draws it. Held
   * with the `chat` it is for, so the panel being replaced never takes it.
   */
  resumeChat: { chat: number; thread: StoredConversation } | null
  /** Opens `thread` in a fresh panel; see `resumeChat`. */
  openChat: (thread: StoredConversation) => void
  /** Hands the reopened conversation over exactly once, to the panel it is for. */
  takeResumeChat: (chat: number) => StoredConversation | null
}

/** A fresh panel's state: what `newChat` sets, and `openChat` before it hands a thread over. */
/** The page the panel is opened on, with the screen's title it has then. */
export interface OpenedPage extends AppPage {
  title: string | null
}

function openedPage(now: MitraState): OpenedPage | null {
  return now.routePage === null ? null : { ...now.routePage, title: now.pageTitle }
}

function freshChat(now: MitraState) {
  return {
    chat: now.chat + 1,
    open: true,
    // A new chat from inside the open panel keeps the page it was opened on.
    openedFrom: now.open ? now.openedFrom : now.companyPage,
    openedOnPage: now.open ? now.openedOnPage : openedPage(now),
    stage: 'closed' as const,
    pendingQuestion: null,
    draftQuestion: null,
    aboutRequest: null,
    resumeChat: null,
  }
}

/**
 * Hoisted so any launcher can open the panel — Spotlight's ask row, the
 * Home ask box, the Ask Mitra bar — and none of them has to know
 * where the panel is mounted.
 *
 * Deliberately NOT persisted, unlike `useUiStore`: a panel that reopened
 * itself on every reload, over whatever page the member arrived at, would be
 * a nuisance rather than a memory. The conversation itself lives in the
 * panel's own component state and dies with the page for the same reason
 * upstream's widget never wrote a transcript to storage.
 */
/** Which `showPage` call published the current `pageTitle`. Not state: no
 *  reader renders from it, it only arbitrates the take-back. */
let pageOwner: symbol | null = null
/** The same arbitration for `companyPage`. */
let companyOwner: symbol | null = null

export const useMitraStore = create<MitraState>()((set, get) => ({
  open: false,
  pendingQuestion: null,
  draftQuestion: null,
  aboutRequest: null,
  pageTitle: null,
  companyPage: null,
  openedFrom: null,
  routePage: null,
  openedOnPage: null,
  stage: 'closed',
  stageHost: null,
  chat: 0,
  resumeChat: null,

  newChat: () => {
    set(freshChat(get()))
  },
  openChat: (thread) => {
    const fresh = freshChat(get())
    set({ ...fresh, resumeChat: { chat: fresh.chat, thread } })
  },
  takeResumeChat: (chat) => {
    const resume = get().resumeChat
    if (resume === null || resume.chat !== chat) return null
    set({ resumeChat: null })
    return resume.thread
  },
  setStage: (stage) => {
    if (get().stage !== stage) set({ stage })
  },
  setStageHost: (host) => {
    if (get().stageHost !== host) set({ stageHost: host })
  },
  showPage: (title) => {
    const trimmed = title.trim()
    const owner = Symbol(trimmed)
    pageOwner = owner
    set({ pageTitle: trimmed || null })
    return () => {
      if (pageOwner !== owner) return
      pageOwner = null
      set({ pageTitle: null })
    }
  },
  showCompany: (symbol) => {
    const owner = Symbol(symbol)
    companyOwner = owner
    set({ companyPage: symbol })
    return () => {
      if (companyOwner !== owner) return
      companyOwner = null
      set({ companyPage: null })
    }
  },
  setRoutePage: (page) => {
    const now = get().routePage
    if (now?.kind !== page?.kind || now?.name !== page?.name) set({ routePage: page })
  },
  openPanel: (question) => {
    const trimmed = question?.trim() ?? ''
    const wasOpen = get().open
    if (!wasOpen) {
      // Opening fresh: nothing was on screen to lose, so a question is
      // asked the instant the panel takes it. A `draftQuestion` left over
      // from a previous, closed session — never read because nothing was
      // open to read it — does not resurface into this one.
      set({
        open: true,
        openedFrom: get().companyPage,
        openedOnPage: openedPage(get()),
        pendingQuestion: trimmed || null,
        draftQuestion: null,
      })
      return
    }
    if (!trimmed) {
      // A blank hand-over is an open request with nothing attached, not a
      // question to ask — and it must not erase one that is still waiting.
      set({ open: true })
      return
    }
    // Already open: a member may have an unsent draft in the composer, or
    // a turn streaming. Routed to `draftQuestion`, never `pendingQuestion`,
    // so it is never read as one and never starts a turn on its own.
    set({ open: true, draftQuestion: trimmed })
  },
  openWithDraft: (draft) => {
    // Never `pendingQuestion`: a draft is the start of a question, and
    // nothing is asked until the member sends it. Not trimmed either — the
    // trailing space after "About Overview:" is where they start typing.
    if (draft.trim() === '') {
      get().openPanel()
      return
    }
    if (get().open) {
      set({ draftQuestion: draft })
      return
    }
    get().openPanel()
    queueMicrotask(() => {
      if (get().open) set({ draftQuestion: draft })
    })
  },
  askAbout: (about) => {
    // Never `pendingQuestion`: an entry point names a subject, and nothing
    // is asked until the reader sends what the composer was filled with.
    set({
      open: true,
      aboutRequest: about,
      ...(get().open ? {} : { openedFrom: get().companyPage, openedOnPage: openedPage(get()) }),
    })
  },
  takeAboutRequest: () => {
    const about = get().aboutRequest
    if (about !== null) set({ aboutRequest: null })
    return about
  },
  closePanel: () => {
    // A Stage is the panel's answer laid out as a page, so it closes with
    // the panel; `stage` is already `closed` whenever the flag is off.
    set(
      get().stage === 'closed'
        ? { open: false, openedFrom: null, openedOnPage: null }
        : { open: false, openedFrom: null, openedOnPage: null, stage: 'closed' },
    )
  },
  takePendingQuestion: () => {
    const question = get().pendingQuestion
    if (question !== null) set({ pendingQuestion: null })
    return question
  },
  takeDraftQuestion: () => {
    const question = get().draftQuestion
    if (question !== null) set({ draftQuestion: null })
    return question
  },
}))
